import {
  AccessToken,
  IDToken,
  TokenManagerInterface,
  UserClaims,
} from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';

interface AuthState {
  userInfo?: UserClaims;
}

type ValueOf<T> = T[keyof T];

export interface AuthActionTypes {
  SET_USER_INFO: 'SET_USER_INFO';
}
export interface AuthAction {
  type: ValueOf<typeof AUTH_ACTION_TYPES>;
  payload?: any;
}
const initialState: AuthState = {};
interface AuthContextProps {
  state: AuthState;
  dispatch: React.Dispatch<AuthAction>;
  logout: () => Promise<void>;
}

const defaultValue: AuthContextProps = {
  state: initialState,
  dispatch: () => {},
  logout: async () => {},
};

const AuthDataContext = createContext<AuthContextProps>(defaultValue);

export const useAuthDataContext = () => useContext(AuthDataContext);

export const AuthDataProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { oktaAuth } = useOktaAuth();
  const [state, dispatch] = useReducer(authReducer, initialState);
  const logout = useCallback(async () => {
    try {
      await oktaAuth.signOut({
        clearTokensBeforeRedirect: true,
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Okta API Error 💥', error);
    }
  }, [oktaAuth]);

  useEffect(() => {
    let ignore = false;
    const getUserInfo = async () => {
      try {
        const tokenManager: TokenManagerInterface = oktaAuth.tokenManager;
        const accessToken: AccessToken = (await tokenManager.get(
          'accessToken'
        )) as AccessToken;
        const idToken: IDToken = (await tokenManager.get('idToken')) as IDToken;
        const userInfo: UserClaims = await oktaAuth.token.getUserInfo(
          accessToken,
          idToken
        );
        if (!ignore) {
          dispatch({
            type: AUTH_ACTION_TYPES.SET_USER_INFO,
            payload: userInfo,
          });
        }
      } catch (error) {
        console.error('Okta API Error 💥', error);
      }
    };
    getUserInfo();
    return () => {
      ignore = true;
    };
  }, [oktaAuth]);
  const contextValue = useMemo(
    () => ({ state, dispatch, logout }),
    [state, dispatch, logout]
  );

  return (
    <AuthDataContext.Provider value={contextValue}>
      {children}
    </AuthDataContext.Provider>
  );
};
export default AuthDataProvider;

export const AUTH_ACTION_TYPES: AuthActionTypes = {
  SET_USER_INFO: 'SET_USER_INFO',
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  const { payload, type } = action;

  switch (type) {
    case AUTH_ACTION_TYPES.SET_USER_INFO:
      return { ...state, userInfo: payload };
    default: {
      throw Error('Unknown action: ' + action.type);
    }
  }
};
